import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, ApiError, type Revision, type Step, type DocRow } from '../api';
import {
  Loading,
  ErrorState,
  StateChip,
  StageBar,
  StageRail,
  CodeChip,
  Due,
  fmtDate,
} from '../components/ui';
import { meta } from '../module';
import { ActionDialog } from '../components/ActionDialog';

type Detail = { document: DocRow & { project: { code: string; name: string } }; revisions: Revision[] };

/** A level is done when every slot in it is. Slots share a level number. */
function ladderProgress(steps: Step[]): number {
  if (steps.length === 0) return 0;
  const done = steps.filter((s) => s.state === 'DONE').length;
  return Math.round((done / steps.length) * 100);
}

function StepRow({
  step,
  onAct,
  myUserId,
}: {
  step: Step;
  onAct: (s: Step) => void;
  myUserId: string;
}) {
  const cls =
    step.state === 'DONE'
      ? step.resolvedCode === 'CODE_3' || step.resolvedCode === 'CODE_4'
        ? 'ret'
        : 'done'
      : step.state === 'AWAITING'
        ? 'now'
        : '';

  const glyph =
    step.state === 'DONE' ? (cls === 'ret' ? '✕' : '✓') : step.state === 'SKIPPED' ? '–' : String(step.level);

  // The action button appears only for the person whose turn it is.
  // Showing it to everyone and rejecting the click server-side is a
  // worse experience than not showing it.
  const mine = step.state === 'AWAITING' && (step.assignee === null || step.assignee.id === myUserId);

  return (
    <div className="lstep">
      <div className={`ldot ${cls}`} aria-hidden="true">
        {glyph}
      </div>
      <div className="lbody">
        <div className="lrole">
          L{step.level} · {step.role}
          {step.discipline ? ` · ${step.discipline.name}` : ''}
        </div>
        <div className="lmeta">
          {step.assignee ? step.assignee.name : 'Unassigned'}
          {step.state === 'AWAITING' && (
            <>
              {' · '}
              <Due dueAt={step.dueAt} />
            </>
          )}
          {step.state === 'NOT_STARTED' && ' · not started'}
          {step.state === 'SKIPPED' && ' · skipped'}
          {step.quorum > 1 && ` · needs ${step.quorum} reviews`}
        </div>

        {step.actions.map((a) => (
          <div key={a.id}>
            <div className="lmeta" style={{ marginTop: 8 }}>
              <CodeChip code={a.code} /> {a.actor.name} · {fmtDate(a.actedAt)}
            </div>
            {a.comment && <div className="lcomment">{a.comment}</div>}
          </div>
        ))}

        {mine && (
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={() => onAct(step)}>
            Review this
          </button>
        )}
      </div>
    </div>
  );
}

export function DocumentDetail({ myUserId }: { myUserId: string }) {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<Detail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [acting, setActing] = useState<Step | null>(null);
  const [openRev, setOpenRev] = useState<string | null>(null);

  async function load() {
    setError(null);
    try {
      const res = await api.get<Detail>(`/documents/${id}`);
      setData(res);
      // Newest revision open by default; the older ones stay one
      // click away rather than being hidden or deleted.
      const newest = res.revisions[res.revisions.length - 1];
      setOpenRev(newest ? newest.id : null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not load this document.');
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={6} />;

  const { document: doc, revisions } = data;
  const current = revisions[revisions.length - 1];

  return (
    <>
      <div className="page-head">
        <div>
          <p style={{ fontSize: '.8125rem', marginBottom: 4 }}>
            <Link to="/" style={{ color: 'var(--accent)' }}>
              ← Documents
            </Link>
          </p>
          <h1>{doc.docNumber}</h1>
          <p>
            {doc.title} · {doc.docType?.name}
            {doc.discipline ? ` · ${doc.discipline.name}` : ''} · {doc.project.code}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <StageBar stage={doc.currentStage} />
          <StateChip state={doc.state} />
        </div>
      </div>

      {meta.usesStages && <StageRail current={doc.currentStage} />}

      <div className="cols">
        {/* ---- left: revision history ---- */}
        <div>
          <h2 style={{ fontSize: '1rem', marginBottom: 12 }}>Revisions</h2>

          {[...revisions].reverse().map((r) => (
            <div className="rev" key={r.id}>
              <button
                className="rev-head"
                onClick={() => setOpenRev(openRev === r.id ? null : r.id)}
                aria-expanded={openRev === r.id}
              >
                <span className="num" style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                  Rev {r.revCode}
                </span>
                {r.stage && <StageBar stage={r.stage} />}
                <StateChip state={r.state} />
                <span className="lmeta" style={{ marginLeft: 'auto' }}>
                  {r.submittedBy?.name} · {fmtDate(r.submittedAt)}
                </span>
              </button>

              {openRev === r.id && (
                <div className="rev-body">
                  {r.fileId && (
                    <p style={{ margin: '10px 0' }}>
                      <a
                        className="btn btn-ghost btn-sm"
                        href={api.fileUrl(r.fileId)}
                        rel="noopener"
                      >
                        Download file
                      </a>
                    </p>
                  )}
                  <div className="ladder">
                    {r.steps.map((s) => (
                      <StepRow key={s.id} step={s} onAct={setActing} myUserId={myUserId} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}

          {revisions.length === 0 && (
            <div className="card">
              <p>No revision has been submitted yet.</p>
            </div>
          )}
        </div>

        {/* ---- right: current cycle ---- */}
        <aside className="card">
          <h2 style={{ fontSize: '1rem' }}>Current cycle</h2>
          {current ? (
            <>
              <p className="lmeta" style={{ marginBottom: 4 }}>
                Rev {current.revCode}
                {current.stage ? ` · ${current.stage}` : ''}
              </p>
              <div className="progress" aria-hidden="true">
                <i style={{ width: `${ladderProgress(current.steps)}%` }} />
              </div>
              <p className="lmeta" style={{ marginBottom: 12 }}>
                {ladderProgress(current.steps)}% of steps complete
              </p>
              <div className="ladder">
                {current.steps.map((s) => (
                  <StepRow key={s.id} step={s} onAct={setActing} myUserId={myUserId} />
                ))}
              </div>
            </>
          ) : (
            <p className="lmeta">Nothing in review.</p>
          )}
        </aside>
      </div>

      {acting && current && (
        <ActionDialog
          stepId={acting.id}
          docNumber={doc.docNumber}
          revCode={current.revCode}
          onClose={() => setActing(null)}
          onDone={() => {
            setActing(null);
            void load();
          }}
        />
      )}
    </>
  );
}
